import { useGetHeightOfAds } from "@components/Advertisement/hooks/useGetHeightOfAds";
import {
  AdSlot,
  ExternalAdSlot,
  ResponsiveReserveHeight,
} from "@sphtech/web2-ads";
import { IS_PLAYWRIGHT } from "@util/constant";
import { cn } from "@util/helpers";

import AdPlaceholder from "./components/AdPlaceholder";
import {
  SDK_AD_SLOT_HOLDER_BASE_CLASS,
  SDK_AD_SLOT_RESERVE_HEIGHTS,
} from "./utils/constants";
import { generateAdId, generateAdSlotName } from "./utils/helpers";
import { AdUnitNames } from "./utils/types";
import { useBtSdkAdsCanaryConfigContext } from "./BtSdkAdsCanaryConfigContext";
import CanaryInfiniteScrollAdSlot from "./CanaryInfiniteScrollAdSlot";

/**
 * Strip legacy `min-h-*` band classes (responsive variants included) from a
 * className before it rides onto the SDK GPT slot div. In the SDK path the
 * reservation (`reserveHeight`) owns the loading min-height; a `min-h-*` left on
 * the holder would re-hold the band under a short creative — the gap
 * `collapseToServedSize` exists to remove — and the SDK can't collapse a class
 * min-height. The visible chrome (bg/padding/border) is kept so it still frames
 * a fill and collapses with an Innity skin breakout that zeroes the slot div.
 */
function stripReserveMinHeight(className?: string): string | undefined {
  if (!className) return className;
  const kept = className
    .split(/\s+/)
    .filter((token) => token && !/^(?:[\w-]+:)*min-h-/.test(token));

  return kept.length ? kept.join(" ") : undefined;
}

type AdProps = {
  adType: AdUnitNames;
  setAdsHeight?: React.Dispatch<React.SetStateAction<number>>;
  counter?: number;
  articleId?: string;
  articleTags?: string;
  gsChannels?: string;
  /**
   * Loading-time reservation height for the SDK slot. Defaults to the balanced
   * band in `SDK_AD_SLOT_RESERVE_HEIGHTS` for this `adType`; pass `false` to opt
   * out of reservation, or an explicit number/map to override per call site.
   */
  reserveHeight?: number | false | ResponsiveReserveHeight;
} & Pick<React.HTMLAttributes<HTMLDivElement>, "className">;

export default function Ad({
  adType,
  className,
  setAdsHeight,
  counter = 0,
  articleId,
  articleTags,
  gsChannels,
  reserveHeight,
}: AdProps): React.ReactElement {
  const sdkAdsConfig = useBtSdkAdsCanaryConfigContext();
  const { boxRef } = useGetHeightOfAds(setAdsHeight);

  const id = generateAdId(adType, counter);
  const slotName = generateAdSlotName(adType, counter);
  const holderClassName = cn(
    SDK_AD_SLOT_HOLDER_BASE_CLASS,
    "flex justify-center"
  );

  const adDataAttributes = {
    "data-testid": id,
    "data-ad-type": adType,
    "data-ad-index": counter,
    "data-ad-article-id": articleId,
    "data-ad-article-tags": articleTags,
    "data-ad-gs-channels": gsChannels,
    ref: boxRef,
  };

  if (sdkAdsConfig && !IS_PLAYWRIGHT) {
    if (adType === "prestitial") return <></>;
    if (adType === "postitial") return <></>;

    if (adType === "catfish") {
      return (
        <ExternalAdSlot
          id={id}
          siteKey={sdkAdsConfig.siteKey}
          placement={adType}
          page={sdkAdsConfig.page}
          className={cn(className, holderClassName)}
        />
      );
    }

    const targeting = {
      ...(articleId ? { btarticleid: articleId } : {}),
      ...(articleTags ? { bttags: articleTags } : {}),
      ...(gsChannels ? { gs_channels: gsChannels } : {}),
    };

    /*
     * Appended infinite-scroll articles (counter > 0, only ever body ads from
     * ParagraphWrapper) drive their own GPT slots through the SDK infinite-scroll
     * primitives. The declarative <AdSlot> below stays for first-paint slots —
     * the first article (counter 0), the top lb1, and the listing — which is
     * what <AdSlot> supports; it is not the SDK's sanctioned path for content
     * appended after the initial render. The latched canary config keeps us in
     * this SDK branch as the URL updates onto sibling articles while scrolling.
     */
    if (sdkAdsConfig.pageType === "article" && counter > 0) {
      return (
        <CanaryInfiniteScrollAdSlot
          adType={adType}
          counter={counter}
          page={sdkAdsConfig.page}
          siteKey={sdkAdsConfig.siteKey}
          targeting={targeting}
          id={id}
          slotName={slotName}
          className={className}
        />
      );
    }

    /*
     * reserveHeight makes the SDK reserve a per-breakpoint min-height (and show
     * its loading skeleton) so the slot holds space before the creative paints,
     * minimising layout shift. The height defaults to the balanced band in
     * SDK_AD_SLOT_RESERVE_HEIGHTS for this adType (overridable via the prop;
     * `false` opts out). The caller's band/layout classes ride on `className` —
     * the GPT slot div the SDK styles and vendor scripts mutate — so an Innity
     * XXL skin breakout that inline-zeroes that div collapses the band chrome
     * with it instead of stranding a grey gap above the header (WEBCR-1554);
     * `wrapperClassName` (the reserving shell the breakout never touches) is left
     * for positioning concerns only. Legacy `min-h-*` band classes are dropped
     * here because the SDK reservation now owns the loading height. See
     * SDK_AD_SLOT_HOLDER_BASE_CLASS for the holder-class contract.
     */
    return (
      <AdSlot
        id={id}
        siteKey={sdkAdsConfig.siteKey}
        placement={adType}
        slotName={slotName}
        page={sdkAdsConfig.page}
        targeting={targeting}
        className={cn(holderClassName, stripReserveMinHeight(className))}
        reserveHeight={
          reserveHeight ?? SDK_AD_SLOT_RESERVE_HEIGHTS[adType] ?? false
        }
      />
    );
  }

  if (IS_PLAYWRIGHT) {
    return <AdPlaceholder className={className} adType={adType} />;
  }

  // Legacy (non-SDK) holder: a single div that is both the styled band and the
  // GPT/DFP render target, mirroring the SDK single-div holder. The band classes
  // ride on the same #id div the legacy ad script fills and an Innity skin
  // breakout inline-zeroes, so the band collapses with the slot instead of
  // stranding a grey gap on a wrapper above it (WEBCR-1554). `min-h-*` stays: the
  // legacy path has no SDK reservation, so the holder's own min-height is the CLS
  // guard (and the breakout's inline `min-height:0` still collapses it).
  return (
    <div
      id={id}
      className={cn(className, holderClassName)}
      {...adDataAttributes}
    />
  );
}
